/**
 * The substantive domains, and how each one answers the dashboard's questions.
 *
 * The four domains are not interchangeable and this file does not pretend otherwise. Sales
 * counts invoices and customers; inventory counts items, warehouses and batches; expenses count
 * transactions and journals. Each backend summary already reports its own domain honestly,
 * including statuses like SOURCE_NOT_SUPPLIED, and an adapter's whole job is to read that
 * summary — never to compute, estimate, or fill a gap.
 *
 * A cell that a domain genuinely cannot answer returns null and renders as NOT_AVAILABLE. That
 * is a real answer: it says this domain does not track this, or its source has not been
 * supplied. It is not the same as zero, and showing zero there would be a fabricated metric.
 */

export const NOT_AVAILABLE = null;

/** Roll several reconciliation statuses into the worst one, so a row shows its weakest link. */
const RANK = {
  SOURCE_NOT_SUPPLIED: 0,
  NOT_RECONCILED: 1,
  DIFFERENCE: 2,
  UNRESOLVED: 2,
  PARTIAL: 3,
  RECONCILED: 4,
  MATCHED: 4,
  OK: 4,
};

export function worstStatus(statuses) {
  const present = (statuses || []).filter((s) => typeof s === 'string' && s.length);
  if (!present.length) return NOT_AVAILABLE;
  return present.reduce((worst, s) => {
    const a = RANK[worst] !== undefined ? RANK[worst] : 99;
    const b = RANK[s] !== undefined ? RANK[s] : 99;
    return b < a ? s : worst;
  });
}

/** Sum only the counts a domain actually reports; null when it reports none of them. */
export function sumPresent(values) {
  const present = (values || []).filter((v) => typeof v === 'number' && Number.isFinite(v));
  return present.length ? present.reduce((a, b) => a + b, 0) : NOT_AVAILABLE;
}

export const DOMAINS = [
  {
    key: 'sales',
    label: 'Penjualan / Pendapatan',
    // The tab value the engagement used before consolidation. Old links still carry it.
    legacyTab: 'sales',
    evidencePhase: 'Penjualan / Pendapatan',
    evidenceModule: 'SalesRevenue',
    summaryPath: (id) => `/engagements/${id}/sales-summary`,
    adapt: (s) => ({
      population: s.sales_rows,
      populationUnit: 'baris',
      populationDetail: [
        s.invoices != null ? `${s.invoices} faktur` : null,
        s.customers != null ? `${s.customers} pelanggan` : null,
      ].filter(Boolean).join(' · ') || NOT_AVAILABLE,
      coverageStatus: s.coverage_status,
      reconciliation: worstStatus([
        s.sales_vs_gl_status, s.sales_vs_vat_status, s.sales_vs_ar_status, s.ar_tie_out_status,
      ]),
      exceptions: sumPresent([
        s.cutoff_exceptions, s.unmatched_sales, s.unmatched_gl, s.unmatched_vat, s.unmatched_ar,
      ]),
      // The sales summary carries no sampling or materiality figure of its own.
      sample: NOT_AVAILABLE,
      materiality: NOT_AVAILABLE,
    }),
  },
  {
    key: 'purchases',
    label: 'Pembelian / Hutang Usaha',
    legacyTab: 'purchases',
    evidencePhase: 'Pembelian / Hutang Usaha',
    evidenceModule: 'PurchasesPayables',
    summaryPath: (id) => `/engagements/${id}/purchases-summary`,
    adapt: (s) => ({
      population: s.purchase_rows,
      populationUnit: 'baris',
      populationDetail: [
        s.documents != null ? `${s.documents} dokumen` : null,
        s.vendors != null ? `${s.vendors} pemasok` : null,
      ].filter(Boolean).join(' · ') || NOT_AVAILABLE,
      coverageStatus: s.coverage_status,
      reconciliation: worstStatus([
        s.purchase_vs_gl_status, s.purchase_vs_vat_status, s.purchase_vs_ap_status,
        s.ap_tie_out_status, s.subsequent_payment_status, s.unrecorded_liability_status,
      ]),
      exceptions: sumPresent([
        s.cutoff_exceptions, s.unmatched_purchase, s.unmatched_gl, s.unmatched_vat,
        s.unmatched_ap, s.unrecorded_liability_candidates,
      ]),
      sample: NOT_AVAILABLE,
      materiality: NOT_AVAILABLE,
    }),
  },
  {
    key: 'inventory',
    label: 'Persediaan',
    legacyTab: 'inventory',
    evidencePhase: 'Persediaan',
    evidenceModule: 'Inventory',
    summaryPath: (id) => `/engagements/${id}/inventory-summary`,
    adapt: (s) => ({
      population: s.items,
      populationUnit: 'item',
      populationDetail: [
        s.warehouses != null ? `${s.warehouses} gudang` : null,
        s.batches != null ? `${s.batches} batch` : null,
      ].filter(Boolean).join(' · ') || NOT_AVAILABLE,
      // Inventory reports a listing selection rather than a period coverage.
      coverageStatus: s.listing_selection_status,
      reconciliation: worstStatus([
        s.inventory_vs_gl_status, s.movement_status, s.monthly_rollforward_status,
        s.receipts_status, s.delivery_cutoff_status,
      ]),
      exceptions: sumPresent([s.cutoff_items, s.negative_rows, s.items_without_movement]),
      sample: NOT_AVAILABLE,
      materiality: NOT_AVAILABLE,
    }),
  },
  {
    key: 'expenses',
    label: 'Beban',
    legacyTab: 'expenses',
    evidencePhase: 'Beban',
    evidenceModule: 'Expenses',
    summaryPath: (id) => `/engagements/${id}/expenses-summary`,
    adapt: (s) => ({
      population: s.transactions,
      populationUnit: 'transaksi',
      populationDetail: [
        s.journals != null ? `${s.journals} jurnal` : null,
        s.categories != null ? `${s.categories} kategori` : null,
      ].filter(Boolean).join(' · ') || NOT_AVAILABLE,
      coverageStatus: s.completeness_status,
      reconciliation: worstStatus([
        s.expense_vs_purchase_status, s.expense_vs_vat_status, s.expense_vs_wht_status,
        s.expense_vs_payment_status, s.expense_vs_bank_status,
      ]),
      exceptions: sumPresent([
        s.cutoff_items, s.anomaly_findings, s.capitalisation_candidates,
      ]),
      // Expenses is the one domain whose summary reports its own sampling and materiality.
      sample: s.samples_selected,
      materiality: s.materiality,
    }),
  },
];

export const DOMAIN_KEYS = DOMAINS.map((d) => d.key);
export const LEGACY_TABS = DOMAINS.map((d) => d.legacyTab);

export function domainFor(key) {
  return DOMAINS.find((d) => d.key === key) || null;
}

/** A legacy `?tab=sales` link names a domain, not a tab, once the four are consolidated. */
export function domainForLegacyTab(tab) {
  const found = DOMAINS.find((d) => d.legacyTab === tab);
  return found ? found.key : null;
}
